/**
 * i18n
 *
 * @author fukui
 */
import { NameI18N } from '@components/activity/src/components/RewardGroup/types';
import iConfig from '@falla/config/src/config';
import { Regions } from '@falla/types/src/types';

import appBridge from '../../app-bridge/src';
import { compareVersion, getBrowserInfos, getQueryParams, getWebHostProdRelativePath } from './common-util';
import { combineURLs, format } from './string-util';

const getLang = () => {
  // 优先通过客户端appBridge提供的语言标识
  const { realVersion, language } = appBridge.getUserDeviceInfo();
  const { isMobile } = getBrowserInfos();
  let appLang = language;
  const defaultLang = navigator.language.toLowerCase();
  /**
   * 旧版本（小于3.20.0）不通过appBridge.getUserDeviceInfo获取语言
   */
  if (appBridge.isAppEnv() && isMobile && compareVersion(realVersion || '', '3.20.0') < 0) {
    appLang = navigator.language.toLowerCase();
  }

  return appLang?.toLowerCase() || defaultLang;
};

interface IGetLanguage {
  defaultLang?: string;
  includesLang?: string[];
}

// 俄罗斯、哈萨克斯坦、 乌兹别克斯坦、吉尔吉斯斯坦、塔吉克斯坦、土库曼斯坦
export const enum CA_LANG_ENUM {
  RUSSIA = 'ru',
  KAZAKHSTAN = 'kk',
  UZBEKISTAN = 'uz',
  KIRGIZSTAN = 'ky',
  TAJIKISTAN = 'tg',
  TURKMENISTAN = 'tk',
}

export const getLanguage = (opt?: IGetLanguage) => {
  const language = getQueryParams('debugI18n') || getLang();
  let locales = opt?.includesLang?.length ? [...opt?.includesLang, 'ru'] : getLocales();

  // 正式环境移除中文标识
  if (iConfig.isHostProd) {
    locales = locales?.filter((locale) => locale !== 'zh');
  }

  /**
   * 中亚区国家语种默认指定俄语
   * 哈萨克斯坦、 乌兹别克斯坦、吉尔吉斯斯坦、塔吉克斯坦、土库曼斯坦
   */

  if (
    [
      CA_LANG_ENUM.KAZAKHSTAN,
      CA_LANG_ENUM.UZBEKISTAN,
      CA_LANG_ENUM.KIRGIZSTAN,
      CA_LANG_ENUM.TAJIKISTAN,
      CA_LANG_ENUM.TURKMENISTAN,
    ].find((item) => language.indexOf(item) > -1)
  ) {
    return 'ru';
  }

  for (const locale of locales) {
    if (language.indexOf(locale) > -1) return locale;
  }
  return opt?.defaultLang || 'tr';
};

export const getLocales = () => {
  return ['en', 'zh', 'ar', 'tr', 'es', 'bn', 'hi', 'pt', 'ur', 'id', 'ms', 'ru', 'kk', 'uz', 'ky', 'tg', 'tk'];
};

export const getI18nTranslationUrl = (translateId: number | string, isHostProd: boolean, isDev: boolean) => {
  return combineURLs(
    `${getWebHostProdRelativePath(isDev)}/lang_conf/`,
    `${isHostProd ? '' : 'pre'}/h5_${translateId}.json`,
  );
};

// zh 中文 => 土语 => 英语
// en 英语 => 土语 => 英语
// ar 阿语 => 土语 => 英语
// tr 土语 => 土语 => 英语
// es 西语 => 土语 => 英语
// pt 葡萄牙语 => 土语 => 英语
// bn 印地语 => 土语 => 英语
// hi 孟加拉语 => 土语 => 英语
// ur 乌尔都语 => 土语 => 英语
// id 印尼 => 土语 => 英语
// ms 马来语 => 土语 => 英语
// ru 俄语 => 土语 => 英语
// kk 哈萨克斯坦 => 俄语 => 土语 => 英语
// uz 乌兹别克斯坦 => 俄语 => 土语 => 英语
// ky 吉尔吉斯斯坦 => 俄语 => 土语 => 英语
// tg 塔吉克斯坦 => 俄语 => 土语 => 英语
// tk 土库曼斯坦 => 俄语 => 土语 => 英语
// 兜底语言
export const langMap: {
  [key in string]: string[];
} = {
  zh: ['tr', 'en'],
  en: ['tr', 'en'],
  ar: ['tr', 'en'],
  tr: ['tr', 'en'],
  es: ['tr', 'en'],
  pt: ['tr', 'en'],
  bn: ['tr', 'en'],
  hi: ['tr', 'en'],
  ur: ['tr', 'en'],
  id: ['tr', 'en'],
  ms: ['tr', 'en'],
  ru: ['tr', 'en'],
  kk: ['ru', 'tr', 'en'],
  uz: ['ru', 'tr', 'en'],
  ky: ['ru', 'tr', 'en'],
  tg: ['ru', 'tr', 'en'],
  tk: ['ru', 'tr', 'en'],
};

// 兜底语言策略实现
export const getI18nText = (nameI18n: NameI18N, lang: keyof NameI18N | string): string => {
  if (!nameI18n) return '';

  if (nameI18n[lang as keyof NameI18N]) return nameI18n[lang as keyof NameI18N];

  const langs = langMap[lang] || [];

  if (langs.length === 0) return nameI18n.en;

  const key = langs.find((v) => nameI18n?.[v as keyof NameI18N]);

  return nameI18n?.[(key as keyof NameI18N) || 'en'];
};

// 转换特殊业务场景的翻译值
export const convertBusinessTranslation = (text: string): string => {
  if (!text) return '';
  let newText = text;

  const { region, countryid } = appBridge.getAppUserInfo();

  // 阿塞拜疆
  if (countryid === 144) {
    newText = newText.replace(/\b(7002|70002|7010)\b/g, '7007');
    newText = newText.replace(/@Yusufcan31/g, '@adelph_24');
    newText = format(newText, { customerId: '7007', serviceRoomIDContact: '7007', telegramId: '@adelph_24' });
    return newText;
  }

  if (region === Regions.CA) {
    newText = newText.replace(/\b(7002|70002|7010)\b/g, '7005');
    newText = newText.replace(/@Yusufcan31/g, '@Yonsuu');
    newText = format(newText, { customerId: '7005', serviceRoomIDContact: '7005', telegramId: '@Yonsuu' });
    return newText;
  }

  newText = format(newText, { customerId: '7002', serviceRoomIDContact: '7002', telegramId: '@elifdn61' });

  return newText;
};

// 是否为中亚国家语种
export const isCaRegionLang = () => {
  const lang = getLanguage() || '';
  return [
    CA_LANG_ENUM.RUSSIA,
    CA_LANG_ENUM.KAZAKHSTAN,
    CA_LANG_ENUM.UZBEKISTAN,
    CA_LANG_ENUM.KIRGIZSTAN,
    CA_LANG_ENUM.TAJIKISTAN,
    CA_LANG_ENUM.TURKMENISTAN,
  ].includes(lang as CA_LANG_ENUM);
};
